import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiBriefcase, FiTag, FiLock, FiAlertCircle } = FiIcons;

const ContactStep = ({ formData, onFormDataChange, showResultsHeading = false }) => {
  const [errors, setErrors] = useState({ name: '', email: '' });

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleChange = (field, value) => {
    onFormDataChange({ ...formData, [field]: value });

    // Clear errors when typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateField = (field, value) => {
    if (field === 'email' && value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    } else if (!value && (field === 'name' || field === 'email')) {
      setErrors(prev => ({ ...prev, [field]: `${field === 'name' ? 'Name' : 'Email'} is required` }));
    }
  };

  const isValid = formData.name.trim() && formData.email.trim() && validateEmail(formData.email);

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-bold text-white"
        >
          {showResultsHeading ? 'Your results are ready!' : 'Quick! Where should we send your results?'}
        </motion.h2>
        <p className="text-base md:text-lg text-gray-300">
          {showResultsHeading
            ? 'Enter your details to unlock your personalised Brand Health Check results'
            : 'Get your personalized Brand Health Report + Top 3 Fixes sent to your inbox'
          }
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="gradient-dark-blue rounded-xl p-6 md:p-8 soft-shadow border border-dark-400 space-y-6 texture-overlay"
      >
        {showResultsHeading && (
          <div className="flex items-center justify-center mb-4 text-primary-600">
            <SafeIcon icon={FiLock} className="w-6 h-6 mr-2" />
            <span className="font-medium">Complete to unlock your results</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 md:mb-3">
              <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
              Your Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              onBlur={(e) => validateField('name', e.target.value)}
              className={`w-full px-4 py-3 bg-dark-400 border ${errors.name ? 'border-danger-500' : 'border-dark-300'} text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base md:text-lg inner-shadow glass-effect`}
              placeholder="Enter your name"
              autoFocus
            />
            {errors.name && (
              <p className="mt-1 text-sm text-danger-500 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 md:mb-3">
              <SafeIcon icon={FiMail} className="w-4 h-4 inline mr-2" />
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={(e) => validateField('email', e.target.value)}
              className={`w-full px-4 py-3 bg-dark-400 border ${errors.email ? 'border-danger-500' : 'border-dark-300'} text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base md:text-lg inner-shadow glass-effect`}
              placeholder="Enter your email"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-danger-500 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />
                {errors.email}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 md:mb-3">
              <SafeIcon icon={FiBriefcase} className="w-4 h-4 inline mr-2" />
              Company Name
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => handleChange('company', e.target.value)}
              className="w-full px-4 py-3 bg-dark-400 border border-dark-300 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base md:text-lg inner-shadow glass-effect"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 md:mb-3">
              <SafeIcon icon={FiTag} className="w-4 h-4 inline mr-2" />
              Role/Title
            </label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full px-4 py-3 bg-dark-400 border border-dark-300 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base md:text-lg inner-shadow glass-effect"
              placeholder="Optional"
            />
          </div>
        </div>

        {!isValid && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center text-sm text-gray-400"
          >
            Please fill in your name and a valid email address to continue
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default ContactStep;